import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Flame, Users, Clock } from "lucide-react";
import { api } from "@/lib/api";

function useCountdown(target) {
  const [left, setLeft] = useState(() => Math.max(0, new Date(target) - new Date()));
  useEffect(() => {
    const t = setInterval(() => setLeft(Math.max(0, new Date(target) - new Date())), 1000);
    return () => clearInterval(t);
  }, [target]);
  const s = Math.floor(left / 1000);
  return {
    d: Math.floor(s / 86400),
    h: Math.floor((s % 86400) / 3600),
    m: Math.floor((s % 3600) / 60),
    s: s % 60,
    live: left <= 0,
  };
}

function Unit({ v, label }) {
  return (
    <div className="flex flex-col items-center">
      <span className="font-heading text-2xl md:text-3xl gold-text tabular-nums">{String(v).padStart(2, "0")}</span>
      <span className="text-[9px] uppercase tracking-wider text-[#A0A0A0]">{label}</span>
    </div>
  );
}

function Countdown({ target }) {
  const c = useCountdown(target);
  if (c.live) return <span className="gold-text font-heading text-xl">Live now 🔴</span>;
  return (
    <div className="flex items-center gap-3">
      {c.d > 0 && <Unit v={c.d} label="days" />}
      <Unit v={c.h} label="hrs" />
      <Unit v={c.m} label="min" />
      <Unit v={c.s} label="sec" />
    </div>
  );
}

export function RitualsStrip() {
  const [rituals, setRituals] = useState([]);
  const load = () => api.get("/rituals").then((r) => setRituals(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const rsvp = async (id) => {
    try {
      const { data } = await api.post(`/rituals/${id}/rsvp`);
      toast.success(data.i_am_going ? "You're on the guest list 🔥" : "Removed from the guest list");
      load();
    } catch (e) { toast.error("Please sign in to RSVP"); }
  };

  if (rituals.length === 0) return null;
  return (
    <div className="mb-10" data-testid="rituals-strip">
      <div className="flex items-center gap-2 mb-1">
        <Flame size={16} className="text-[#D4AF37]" />
        <span className="overline">Weekly Rituals · recurring themed nights</span>
      </div>
      <p className="text-sm text-[#A0A0A0] mb-4">Every week, like clockwork. Set a reminder and don't miss the fun.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {rituals.map((r, i) => (
          <motion.div key={r.id} data-testid={`ritual-${r.id}`}
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            className="pe-card rounded-2xl overflow-hidden">
            <div style={{ height: 4, background: r.accent }} />
            <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-heading text-2xl">{r.title}</h3>
                  <div className="text-xs" style={{ color: r.accent }}>{r.theme}</div>
                </div>
                <span className="text-[11px] text-[#A0A0A0] flex items-center gap-1 flex-shrink-0"><Users size={12} /> {r.going_count} going</span>
              </div>
              <p className="text-sm text-[#A0A0A0] mt-2 leading-relaxed">{r.blurb}</p>
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2 text-[#A0A0A0] text-xs"><Clock size={13} className="text-[#D4AF37]" /> starts in</div>
                <Countdown target={r.next_occurrence} />
              </div>
              <button data-testid={`ritual-rsvp-${r.id}`} onClick={() => rsvp(r.id)}
                className={`w-full mt-4 rounded-full py-2 text-sm font-medium transition-all ${r.i_am_going ? "btn-outline-gold" : "btn-gold"}`}>
                {r.i_am_going ? "You're in ✓ — remind me" : "I'm interested"}
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export function NextRitualBanner() {
  const [ritual, setRitual] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/rituals").then((r) => setRitual(r.data?.[0] || null)).catch(() => {});
  }, []);
  if (!ritual) return null;
  return (
    <section className="px-6 md:px-12 py-8 border-t border-[#2A2A2A]">
      <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        data-testid="landing-next-ritual"
        className="max-w-4xl mx-auto pe-card rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-5"
        style={{ borderColor: `${ritual.accent}55` }}>
        <div className="text-center md:text-left">
          <div className="overline mb-1" style={{ color: ritual.accent }}>Next in the Empire</div>
          <h3 className="font-heading text-3xl">{ritual.title}</h3>
          <p className="text-sm text-[#A0A0A0] mt-1 max-w-md">{ritual.blurb}</p>
        </div>
        <div className="flex flex-col items-center gap-3">
          <Countdown target={ritual.next_occurrence} />
          <button data-testid="ritual-join-cta" onClick={() => navigate("/auth")}
            className="btn-gold px-6 py-2 rounded-full text-sm whitespace-nowrap">Join to attend</button>
        </div>
      </motion.div>
    </section>
  );
}
